import React, { useEffect, useId, useRef } from 'react'
import { lightImpactVibrate } from '../../utils/appBridge'
import { getBottomPadding } from '../../utils/sessionStorage'

interface BottomSheetProps {
  logoSrc?: string
  logoSvg?: React.ReactNode
  heading?: string
  headingComponent?: React.ReactNode
  /** Stable id for string `heading` (prefer over useId when pairing with VaulDrawer titleId) */
  headingId?: string
  /** Id of visible heading inside headingComponent for aria-labelledby */
  headingTitleId?: string
  /** Override default Close control accessible name (Power select_B28 / B29) */
  closeButtonAriaLabel?: string
  /** Hint via aria-description — keep out of aria-label to avoid duplicate announcement */
  closeButtonAriaDescription?: string
  bottomSheetClassName?: string
  bottomSheetHeadClassName?: string
  bottomSheetHeadingTestLocator?: string
  bottomSheetHeadingClassName?: string
  bottomSheetHeadingComponentClassName?: string
  bottomSheetBodyClassName?: string
  bottomSheetCtas?: JSX.Element
  bgWhiteRequired?: boolean
  bottomSheetCtaClassName?: string
  bottomSheetCtaWrapperClassName?: string
  onDismiss: () => void
  children?: any
}

const BottomSheetContent: React.FC<BottomSheetProps> = ({
  logoSrc,
  logoSvg,
  heading,
  headingComponent,
  headingId: headingIdProp,
  headingTitleId,
  closeButtonAriaLabel = 'Close dialog',
  closeButtonAriaDescription = 'double tap to close dialog',
  bottomSheetClassName = '',
  bottomSheetHeadClassName = '',
  bottomSheetHeadingTestLocator = '',
  bottomSheetHeadingClassName = '',
  bottomSheetHeadingComponentClassName = '',
  bottomSheetBodyClassName = '',
  bottomSheetCtas,
  bottomSheetCtaClassName = '',
  bottomSheetCtaWrapperClassName = '',
  onDismiss,
  bgWhiteRequired = true,
  children
}) => {
  const generatedHeadingId = useId()
  const headingId = headingIdProp ?? generatedHeadingId
  const headingRef = useRef<HTMLHeadingElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const focusHeading = () => {
      // Stop re-focusing once the user has moved to a control inside the sheet
      const activeElement = document.activeElement
      if (activeElement && activeElement !== headingRef.current && dialogRef.current?.contains(activeElement)) {
        return
      }
      headingRef.current?.focus({ preventScroll: true })
    }
    focusHeading()
    // Sheets slide in over 500ms; TalkBack/VoiceOver ignore focus on off-screen nodes, so re-assert once settled
    const timers = [0, 50, 150, 300, 550].map((delay) => window.setTimeout(focusHeading, delay))
    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [])

  return (
    <div
      ref={dialogRef}
      role='dialog'
      aria-modal='true'
      aria-labelledby={heading ? headingId : headingTitleId}
      className={`relative ${bottomSheetClassName}`}
    >
      <div className={`sticky z-10 top-0 ${bgWhiteRequired ? 'bg-white' : ''}`}>
        <div className={`flex flex-col pt-6 ${bottomSheetHeadClassName}`}>
          {/* Logo Section */}
          {logoSrc && (
            <div className='flex items-center mb-4 px-5' aria-hidden='true'>
              <img src={logoSrc} alt='' className='object-contain' />
            </div>
          )}
          {
            logoSvg && (
              <div className='flex items-center mb-4 px-5' aria-hidden='true'>
                {logoSvg}
              </div>
            )
          }

          {heading && (
            <h2
              id={headingId}
              ref={headingRef}
              tabIndex={-1}
              test-locator={bottomSheetHeadingTestLocator}
              className={`text-[22px] font-normal mb-4 px-5 outline-none ${bottomSheetHeadingClassName}`}
            >
              {heading}
            </h2>
          )}

          {headingComponent && <div className={`${bottomSheetHeadingComponentClassName}`}>{headingComponent}</div>}
        </div>
      </div>

      {/* Body Section */}
      {children && (
        <div className={`flex flex-col h-full overflow-y-hidden pb-3 px-5 ${bottomSheetBodyClassName}`}>{children}</div>
      )}

      {/* Dismiss Button */}
      <div className={`sticky bottom-0 ${bgWhiteRequired ? 'bg-white' : ''} ${bottomSheetCtaWrapperClassName}`}>
        <div
          style={{ paddingBottom: `${getBottomPadding() ? getBottomPadding() + 4 : 28}px` }}
          className={`pt-5 pb-7 px-[18px] ${bottomSheetCtaClassName}`}
        >
          {bottomSheetCtas ?? (
            <button
              type='button'
              aria-label={closeButtonAriaLabel}
              aria-description={closeButtonAriaDescription}
              onClick={() => {
                lightImpactVibrate()
                onDismiss()
              }}
              className='w-full py-2 px-4 pb-[11px] bg-[#232323] text-white font-medium rounded-xl h-14 
        outline-none focus:outline-none'
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default BottomSheetContent
